import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Card, Grid, Container, useMediaQuery, useTheme } from "@mui/material";
import { keyframes } from "@mui/system";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

/* ===== Paleta (usa seus verdes) ===== */
const BRAND = {
    main: "#036D3D",
    dark: "#004C2A",
    light: "#05A055",
    line: "rgba(3,109,61,.18)",
    cardBorder: "rgba(3,109,61,.10)",
    text: "#24323A",
    textSoft: "#5A6B73",
};

/* ===== Micro-animação ===== */
const rise = keyframes`
  0% { opacity: 0; transform: translateY(12px) }
  100% { opacity: 1; transform: translateY(0) }
`;

/* ===== Hook: anima on scroll ===== */
const useReveal = (threshold = 0.15) => {
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) {
                    setShow(true);
                    obs.unobserve(e.target);
                }
            },
            { threshold }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, show];
};

/* ====== Card Base ====== */
const InfoCard = ({ title, children, delay = 0 }) => {
    const [ref, show] = useReveal(0.2);
    return (
        <Card
            ref={ref}
            sx={{
                p: { xs: 2.75, md: 3.25 },
                height: "100%",
                position: "relative",
                background: "rgba(255,255,255,0.96)",
                borderRadius: 3,
                backdropFilter: "saturate(1.05) blur(8px)",
                opacity: show ? 1 : 0,
                transform: show ? "none" : "translateY(12px)",
                animation: show ? `${rise} .45s ease both` : "none",
                animationDelay: `${delay}ms`,

            }}
        >
            <MKTypography
                variant="button"
                sx={{
                    display: "block",
                    color: BRAND.main,
                    fontWeight: 800,
                    letterSpacing: ".8px",
                    textTransform: "uppercase",
                    fontSize: { xs: ".86rem", md: ".92rem" },
                }}
            >
                {title}
            </MKTypography>

            {children}
        </Card>
    );
};

InfoCard.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    delay: PropTypes.number,
};

/* ====== Seção ====== */
export default function NossaFilosofia({
    backgroundImage, // ex: bgFilosofia
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [hdrRef, hdrShow] = useReveal(0.1);

    const parallax = isMobile ? "scroll" : "fixed";

    return (
        <MKBox
            component="section"
            sx={{
                position: "relative",
                py: { xs: 1, md: 5 },
                backgroundImage: backgroundImage ? `url(${backgroundImage})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundAttachment: parallax,
                "@media (prefers-reduced-motion: reduce)": { backgroundAttachment: "scroll" },

                /* overlays enxutos: contraste bom e imagem presente */
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                        "radial-gradient(900px 360px at 50% 0%, rgba(0,0,0,.10), transparent 55%)",
                    pointerEvents: "none",
                },
                "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(180deg, rgba(255,255,255,.18) 0%, rgba(255,255,255,.22) 28%, rgba(255,255,255,.16) 100%)",
                    pointerEvents: "none",
                },
            }}
        >
            {/* Grain super leve para textura */}
            <MKBox
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    opacity: 0.05,
                    background:
                        "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22240%22 height=%22240%22 viewBox=%220 0 240 240%22><g fill=%22%23036D3D%22 opacity=%22.16%22><circle cx=%2220%22 cy=%2220%22 r=%221%22/><circle cx=%22120%22 cy=%2280%22 r=%221%22/><circle cx=%22210%22 cy=%22130%22 r=%221%22/><circle cx=%2280%22 cy=%22190%22 r=%221%22/></g></svg>')",
                    zIndex: 0,
                }}
            />

            <Container sx={{ maxWidth: 1120, mx: "auto", px: { xs: 2, md: 3 }, position: "relative", zIndex: 1 }}>
                {/* Header */}
                <MKBox
                    ref={hdrRef}
                    sx={{
                        textAlign: "center",
                        mb: { xs: 5, md: 6 },
                        opacity: hdrShow ? 1 : 0,
                        transform: hdrShow ? "none" : "translateY(12px)",
                        animation: hdrShow ? `${rise} .45s ease both` : "none",
                    }}
                >
                    <MKBox
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 0.75,
                            borderRadius: 999,
                            background: "rgba(255,255,255,.85)",
                            boxShadow: "0 6px 18px rgba(0,0,0,.06)",
                        }}
                    >
                        <MKTypography
                            variant="button"
                            sx={{
                                color: BRAND.main,
                                fontWeight: 800,
                                letterSpacing: "1.4px",
                                textTransform: "uppercase",
                                fontSize: { xs: ".82rem", md: ".88rem" },
                            }}
                        >
                            Nossa Filosofia de Trabalho
                        </MKTypography>
                    </MKBox>


                </MKBox>

                {/* Cards */}
                <Grid container spacing={{ xs: 2.5, md: 3.25 }}>
                    <Grid item xs={12} md={4}>
                        <InfoCard title="Nossa Missão" delay={0}>
                            <MKTypography
                                variant="body2"
                                sx={{
                                    color: BRAND.text,
                                    lineHeight: 1.78,
                                    textAlign: "justify",
                                    "& strong": { color: BRAND.dark, fontWeight: 700 },
                                }}
                                dangerouslySetInnerHTML={{
                                    __html:
                                        "Consiste na prestação de serviços de <strong>usinagem de peças em geral</strong>, máquinas especiais, projeto e desenvolvimento mecânico de equipamentos, com <strong>mão-de-obra qualificada</strong>, e através da qualidade dos nossos produtos e serviços, buscar continuamente a <strong>satisfação e confiança</strong> dos nossos clientes.",
                                }}
                            />
                        </InfoCard>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <InfoCard title="A Visão" delay={120}>
                            <MKTypography
                                variant="body2"
                                sx={{ color: BRAND.text, lineHeight: 1.78, textAlign: "justify" }}
                                dangerouslySetInnerHTML={{
                                    __html:
                                        "Tornar-se <strong>referência no segmento de usinagem</strong> nacional e internacional e ser reconhecida como uma <strong>empresa inovadora e ética</strong>.",
                                }}
                            />
                        </InfoCard>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <InfoCard title="Nossos Valores" delay={240}>
                            <MKBox
                                component="ul"
                                sx={{
                                    listStyle: "none",
                                    p: 0,
                                    m: 0,
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, // 1 coluna no mobile, 2 no desktop
                                    gap: 1.25, // espaçamento consistente
                                }}
                            >
                                {[
                                    "Comprometimento",
                                    "Sustentabilidade",
                                    "Credibilidade",
                                    "Honestidade",
                                    "Inovação",
                                    "Qualidade focada no atendimento das expectativas dos clientes",
                                ].map((v) => (
                                    <MKBox
                                        key={v}
                                        component="li"
                                        sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}
                                    >
                                        <MKBox
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                mt: 1,
                                                borderRadius: "50%",
                                                background: BRAND.main,
                                                flex: "0 0 auto",
                                            }}
                                        />
                                        <MKTypography
                                            variant="body2"
                                            sx={{ color: BRAND.text, lineHeight: 1.65, wordBreak: "break-word" }}
                                        >
                                            {v}
                                        </MKTypography>
                                    </MKBox>
                                ))}
                            </MKBox>
                        </InfoCard>
                    </Grid>

                </Grid>
            </Container>
        </MKBox>
    );
}

NossaFilosofia.propTypes = {
    backgroundImage: PropTypes.string, // passe bgFilosofia aqui
};
